import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Target,
  Loader2,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";
import { type UserStory } from "@/lib/projectStore";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
  userStories?: UserStory[];
}

interface KeyResult {
  id: string;
  objective_id: string;
  title: string;
  current_value: number;
  target_value: number;
  unit: string;
}

interface Objective {
  id: string;
  title: string;
  description: string;
  target_date: string;
  status: "active" | "completed" | "paused";
  key_results: KeyResult[];
  created_at: string;
}

const OBJ_STATUS_COLORS: Record<string, string> = {
  active: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  paused: "bg-gray-100 text-gray-600 border-gray-300",
};

function krProgress(kr: KeyResult): number {
  if (!kr.target_value || kr.target_value === 0) return 0;
  return Math.min(100, Math.round((kr.current_value / kr.target_value) * 100));
}

function objOverallProgress(objective: Objective): number {
  if (!objective.key_results || objective.key_results.length === 0) return 0;
  const total = objective.key_results.reduce(
    (sum, kr) => sum + krProgress(kr),
    0
  );
  return Math.round(total / objective.key_results.length);
}

function progressColor(pct: number): string {
  if (pct >= 80) return "bg-green-500";
  if (pct >= 50) return "bg-blue-500";
  if (pct >= 25) return "bg-amber-500";
  return "bg-gray-300";
}

const EMPTY_OBJ_FORM = {
  title: "",
  description: "",
  target_date: "",
  status: "active" as Objective["status"],
};

const EMPTY_KR_FORM = {
  title: "",
  current_value: 0,
  target_value: 100,
  unit: "%",
};

export default function OKRPanel({ baProjectId, userStories = [] }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(false);

  // Objective form
  const [showObjForm, setShowObjForm] = useState(false);
  const [editingObjId, setEditingObjId] = useState<string | null>(null);
  const [objForm, setObjForm] = useState({ ...EMPTY_OBJ_FORM });
  const [savingObj, setSavingObj] = useState(false);
  const [deleteObjId, setDeleteObjId] = useState<string | null>(null);

  // Key result form
  const [showKRForm, setShowKRForm] = useState(false);
  const [krForObjId, setKrForObjId] = useState<string | null>(null);
  const [editingKRId, setEditingKRId] = useState<string | null>(null);
  const [krForm, setKRForm] = useState({ ...EMPTY_KR_FORM });
  const [savingKR, setSavingKR] = useState(false);
  const [deleteKRId, setDeleteKRId] = useState<string | null>(null);

  // Expanded objectives
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (baProjectId) load();
    else setObjectives([]);
  }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/api/tools/objectives?project_id=${baProjectId}`,
        { headers: authHeader }
      );
      setObjectives(res.data || []);
    } catch (e) {
      console.error("Failed to load objectives", e);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Objective CRUD ----
  const openNewObj = () => {
    setEditingObjId(null);
    setObjForm({ ...EMPTY_OBJ_FORM });
    setShowObjForm(true);
  };

  const openEditObj = (obj: Objective) => {
    setEditingObjId(obj.id);
    setObjForm({
      title: obj.title,
      description: obj.description,
      target_date: obj.target_date ? obj.target_date.split("T")[0] : "",
      status: obj.status,
    });
    setShowObjForm(true);
  };

  const saveObj = async () => {
    if (!objForm.title.trim()) return;
    setSavingObj(true);
    try {
      if (editingObjId) {
        await axios.patch(
          `${BASE}/api/tools/objectives/${editingObjId}`,
          objForm,
          { headers: authHeader }
        );
      } else {
        const res = await axios.post(
          `${BASE}/api/tools/objectives`,
          { ...objForm, project_id: baProjectId },
          { headers: authHeader }
        );
        // Auto-expand newly created objective
        setExpandedIds((prev) => { const s = new Set(prev); s.add(res.data?.id); return s; });
      }
      setShowObjForm(false);
      setObjForm({ ...EMPTY_OBJ_FORM });
      setEditingObjId(null);
      load();
    } catch (e) {
      console.error("Failed to save objective", e);
    } finally {
      setSavingObj(false);
    }
  };

  const removeObj = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/objectives/${id}`, {
        headers: authHeader,
      });
      setDeleteObjId(null);
      load();
    } catch (e) {
      console.error("Failed to delete objective", e);
    }
  };

  // ---- Key Result CRUD ----
  const openNewKR = (objId: string) => {
    setKrForObjId(objId);
    setEditingKRId(null);
    setKRForm({ ...EMPTY_KR_FORM });
    setShowKRForm(true);
  };

  const openEditKR = (kr: KeyResult) => {
    setKrForObjId(kr.objective_id);
    setEditingKRId(kr.id);
    setKRForm({
      title: kr.title,
      current_value: kr.current_value,
      target_value: kr.target_value,
      unit: kr.unit || "%",
    });
    setShowKRForm(true);
  };

  const saveKR = async () => {
    if (!krForm.title.trim()) return;
    setSavingKR(true);
    try {
      if (editingKRId) {
        await axios.patch(
          `${BASE}/api/tools/key-results/${editingKRId}`,
          krForm,
          { headers: authHeader }
        );
      } else {
        await axios.post(
          `${BASE}/api/tools/key-results`,
          { ...krForm, objective_id: krForObjId },
          { headers: authHeader }
        );
      }
      setShowKRForm(false);
      setKRForm({ ...EMPTY_KR_FORM });
      setEditingKRId(null);
      setKrForObjId(null);
      load();
    } catch (e) {
      console.error("Failed to save key result", e);
    } finally {
      setSavingKR(false);
    }
  };

  const removeKR = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/key-results/${id}`, {
        headers: authHeader,
      });
      setDeleteKRId(null);
      load();
    } catch (e) {
      console.error("Failed to delete key result", e);
    }
  };

  if (!baProjectId) {
    return (
      <div className="h-full flex items-center justify-center text-[#5C5C57]">
        <p className="text-sm">No project selected.</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-[#314E6E]" />
          <h2 className="text-lg font-semibold text-[#1E1E1C]">OKRs</h2>
          <span className="text-xs text-[#5C5C57] bg-[#F7F4EE] border border-[#D9D4CC] rounded-full px-2 py-0.5">
            {objectives.length}
          </span>
        </div>
        <Button
          size="sm"
          onClick={openNewObj}
          className="bg-[#314E6E] hover:bg-[#243b54] text-white gap-1"
        >
          <Plus className="h-4 w-4" />
          Add Objective
        </Button>
      </div>

      {/* Delivery Signal */}
      {userStories.length > 0 && (() => {
        const total = userStories.length;
        const done = userStories.filter(s => s.status === "done").length;
        const inProg = userStories.filter(s => s.status === "in-progress").length;
        const pct = Math.round((done / total) * 100);
        const mustDone = userStories.filter(s => s.priority === "must" && s.status === "done").length;
        const mustTotal = userStories.filter(s => s.priority === "must").length;
        return (
          <div className="mb-5 bg-[#F7F4EE] border border-[#D9D4CC] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-[#314E6E]" />
              <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide">Delivery Signal</p>
              <span className="text-xs text-[#5C5C57] ml-auto">{done}/{total} stories done</span>
            </div>
            <div className="h-2 bg-[#E8E3DB] rounded-full overflow-hidden mb-3">
              <div
                className={`h-full rounded-full transition-all duration-300 ${pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-blue-500" : pct >= 25 ? "bg-amber-500" : "bg-gray-300"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white/60 rounded-lg px-2 py-1.5">
                <p className="text-lg font-bold text-[#1E1E1C]">{done}</p>
                <p className="text-[10px] text-[#5C5C57]">Done</p>
              </div>
              <div className="bg-white/60 rounded-lg px-2 py-1.5">
                <p className="text-lg font-bold text-[#1E1E1C]">{inProg}</p>
                <p className="text-[10px] text-[#5C5C57]">In Progress</p>
              </div>
              <div className="bg-white/60 rounded-lg px-2 py-1.5">
                <p className="text-lg font-bold text-[#1E1E1C]">{mustTotal > 0 ? `${mustDone}/${mustTotal}` : "—"}</p>
                <p className="text-[10px] text-[#5C5C57]">Must-Haves</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[#314E6E]" />
        </div>
      )}

      {/* Empty state */}
      {!loading && objectives.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-[#5C5C57]">
          <Target className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">No objectives set yet.</p>
          <p className="text-xs mt-1 opacity-70">
            Define objectives and track key results.
          </p>
        </div>
      )}

      {/* Objectives accordion */}
      {!loading && objectives.length > 0 && (
        <div className="space-y-4">
          {objectives.map((obj) => {
            const expanded = expandedIds.has(obj.id);
            const overallPct = objOverallProgress(obj);
            const krCount = obj.key_results?.length || 0;

            return (
              <div
                key={obj.id}
                className="bg-[#FCFBF8] border border-[#D9D4CC] rounded-xl overflow-hidden"
              >
                {/* Objective header */}
                <div
                  className="p-4 cursor-pointer hover:bg-[#F7F4EE] transition-colors"
                  onClick={() => toggleExpand(obj.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Progress ring / circle */}
                    <div className="shrink-0 mt-0.5">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                          <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke="#D9D4CC"
                            strokeWidth="3"
                          />
                          <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="none"
                            stroke={
                              overallPct >= 80
                                ? "#22c55e"
                                : overallPct >= 50
                                ? "#3b82f6"
                                : overallPct >= 25
                                ? "#f59e0b"
                                : "#D9D4CC"
                            }
                            strokeWidth="3"
                            strokeDasharray={`${(overallPct / 100) * 94.2} 94.2`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[#1E1E1C]">
                          {overallPct}%
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-[#1E1E1C] text-sm leading-snug">
                          {obj.title}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-xs capitalize ${OBJ_STATUS_COLORS[obj.status]}`}
                        >
                          {obj.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#5C5C57] flex-wrap">
                        {obj.target_date && (
                          <span>
                            Target:{" "}
                            {new Date(obj.target_date).toLocaleDateString(
                              "en-AU",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </span>
                        )}
                        <span>
                          {krCount} key result{krCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                      {obj.description && !expanded && (
                        <p className="text-xs text-[#5C5C57] mt-1 line-clamp-1">
                          {obj.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditObj(obj);
                        }}
                        className="p-1.5 rounded-lg hover:bg-[#F7F4EE] text-[#5C5C57] hover:text-[#1E1E1C] transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteObjId(obj.id);
                        }}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-[#5C5C57] hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      {expanded ? (
                        <ChevronUp className="h-4 w-4 text-[#5C5C57]" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#5C5C57]" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded: description + key results */}
                {expanded && (
                  <div className="border-t border-[#D9D4CC] px-4 pb-4 pt-3 space-y-4">
                    {obj.description && (
                      <p className="text-sm text-[#5C5C57]">{obj.description}</p>
                    )}

                    {/* Key Results */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-[#314E6E] uppercase tracking-wide flex items-center gap-1.5">
                          <TrendingUp className="h-3.5 w-3.5" />
                          Key Results
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewKR(obj.id);
                          }}
                          className="h-7 px-2 text-xs border-[#D9D4CC] text-[#5C5C57] hover:text-[#314E6E] hover:border-[#314E6E]"
                        >
                          <Plus className="h-3 w-3 mr-0.5" />
                          Add KR
                        </Button>
                      </div>

                      {(!obj.key_results || obj.key_results.length === 0) && (
                        <p className="text-xs text-[#5C5C57] opacity-60 py-2">
                          No key results yet. Add one to track progress.
                        </p>
                      )}

                      <div className="space-y-3">
                        {(obj.key_results || []).map((kr) => {
                          const pct = krProgress(kr);
                          return (
                            <div key={kr.id} className="group">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-sm text-[#1E1E1C] leading-snug flex-1 min-w-0">
                                  {kr.title}
                                </p>
                                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => openEditKR(kr)}
                                    className="p-1 rounded hover:bg-[#F7F4EE] text-[#5C5C57] hover:text-[#1E1E1C] transition-colors"
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteKRId(kr.id)}
                                    className="p-1 rounded hover:bg-red-50 text-[#5C5C57] hover:text-red-600 transition-colors"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-[#E8E3DB] rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-300 ${progressColor(pct)}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="text-xs text-[#5C5C57] shrink-0 w-20 text-right">
                                  {kr.current_value}/{kr.target_value}
                                  {kr.unit && ` ${kr.unit}`}
                                  <span className="ml-1 font-medium text-[#1E1E1C]">
                                    ({pct}%)
                                  </span>
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Objective Add/Edit Dialog */}
      <Dialog open={showObjForm} onOpenChange={setShowObjForm}>
        <DialogContent className="max-w-lg bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">
              {editingObjId ? "Edit Objective" : "New Objective"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={objForm.title}
                onChange={(e) =>
                  setObjForm({ ...objForm, title: e.target.value })
                }
                placeholder="e.g. Improve customer satisfaction"
                className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Description
              </label>
              <Textarea
                value={objForm.description}
                onChange={(e) =>
                  setObjForm({ ...objForm, description: e.target.value })
                }
                placeholder="What does this objective aim to achieve?"
                rows={3}
                className="border-[#D9D4CC] bg-white text-[#1E1E1C] resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Target Date
                </label>
                <Input
                  type="date"
                  value={objForm.target_date}
                  onChange={(e) =>
                    setObjForm({ ...objForm, target_date: e.target.value })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Status
                </label>
                <Select
                  value={objForm.status}
                  onValueChange={(v) =>
                    setObjForm({
                      ...objForm,
                      status: v as Objective["status"],
                    })
                  }
                >
                  <SelectTrigger className="border-[#D9D4CC] bg-white text-[#1E1E1C]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowObjForm(false)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={saveObj}
              disabled={savingObj || !objForm.title.trim()}
              className="bg-[#314E6E] hover:bg-[#243b54] text-white"
            >
              {savingObj && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingObjId ? "Save Changes" : "Create Objective"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* KR Add/Edit Dialog */}
      <Dialog open={showKRForm} onOpenChange={setShowKRForm}>
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">
              {editingKRId ? "Edit Key Result" : "Add Key Result"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                Key Result <span className="text-red-500">*</span>
              </label>
              <Input
                value={krForm.title}
                onChange={(e) =>
                  setKRForm({ ...krForm, title: e.target.value })
                }
                placeholder="e.g. Increase NPS score to 70"
                className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Current
                </label>
                <Input
                  type="number"
                  value={krForm.current_value}
                  onChange={(e) =>
                    setKRForm({
                      ...krForm,
                      current_value: Number(e.target.value),
                    })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Target
                </label>
                <Input
                  type="number"
                  value={krForm.target_value}
                  onChange={(e) =>
                    setKRForm({
                      ...krForm,
                      target_value: Number(e.target.value),
                    })
                  }
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5C57] mb-1 block">
                  Unit
                </label>
                <Input
                  value={krForm.unit}
                  onChange={(e) =>
                    setKRForm({ ...krForm, unit: e.target.value })
                  }
                  placeholder="%"
                  className="border-[#D9D4CC] bg-white text-[#1E1E1C]"
                />
              </div>
            </div>
            {krForm.target_value > 0 && (
              <div>
                <div className="flex justify-between text-xs text-[#5C5C57] mb-1">
                  <span>Progress</span>
                  <span className="font-medium text-[#1E1E1C]">
                    {Math.min(
                      100,
                      Math.round(
                        (krForm.current_value / krForm.target_value) * 100
                      )
                    )}
                    %
                  </span>
                </div>
                <div className="h-2 bg-[#E8E3DB] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${progressColor(
                      Math.min(
                        100,
                        Math.round(
                          (krForm.current_value / krForm.target_value) * 100
                        )
                      )
                    )}`}
                    style={{
                      width: `${Math.min(
                        100,
                        Math.round(
                          (krForm.current_value / krForm.target_value) * 100
                        )
                      )}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowKRForm(false)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={saveKR}
              disabled={savingKR || !krForm.title.trim()}
              className="bg-[#314E6E] hover:bg-[#243b54] text-white"
            >
              {savingKR && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingKRId ? "Save Changes" : "Add KR"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Objective confirm */}
      <Dialog open={!!deleteObjId} onOpenChange={() => setDeleteObjId(null)}>
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Delete Objective?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5C57] mt-1">
            This will also delete all associated key results. This action cannot
            be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteObjId(null)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteObjId && removeObj(deleteObjId)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete KR confirm */}
      <Dialog open={!!deleteKRId} onOpenChange={() => setDeleteKRId(null)}>
        <DialogContent className="max-w-sm bg-[#FCFBF8] border border-[#D9D4CC]">
          <DialogHeader>
            <DialogTitle className="text-[#1E1E1C]">Delete Key Result?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5C57] mt-1">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteKRId(null)}
              className="border-[#D9D4CC] text-[#5C5C57]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => deleteKRId && removeKR(deleteKRId)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
